import { BASEURL, STORAGE_KEYS } from "./constants";

/**
 * Refresh access token using refresh token from localStorage
 * @returns {Promise<string|null>} New access token or null if refresh failed
 */
export const refreshAccessToken = async () => {
  try {
    const currentUserDetails = JSON.parse(
      localStorage.getItem(STORAGE_KEYS.SESSION) || "{}"
    );

    if (!currentUserDetails?.refresh) {
      console.warn("No refresh token available");
      return null;
    }

    const response = await fetch(`${BASEURL}/api/auth/token/refresh/`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refresh: currentUserDetails.refresh }),
    });

    if (!response.ok) {
      console.error("Token refresh failed:", response.status);
      return null;
    }

    const result = await response.json();

    // Update token in localStorage
    currentUserDetails.token = result.access;
    localStorage.setItem(STORAGE_KEYS.SESSION, JSON.stringify(currentUserDetails));

    return result.access;
  } catch (error) {
    console.error("Error refreshing token:", error);
    return null;
  }
};

/**
 * Make authenticated API request with automatic token refresh on 401
 * ALWAYS gets fresh token from localStorage (no stale closures)
 *
 * @param {string} url - API endpoint URL
 * @param {object} options - Fetch options (method, body, headers, etc.)
 * @returns {Promise<Response>} Fetch response
 */
export const apiRequest = async (url, options = {}) => {
  // CRITICAL: Always get fresh token from localStorage, never from closure
  const getCurrentToken = () => {
    const userDetails = JSON.parse(
      localStorage.getItem(STORAGE_KEYS.SESSION) || "{}"
    );
    return userDetails.token;
  };

  let token = getCurrentToken();

  if (!token) {
    throw new Error("No authentication token available");
  }

  const defaultHeaders = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  // First attempt with current token
  let response = await fetch(url, {
    ...options,
    headers: { ...defaultHeaders, ...options.headers },
  });

  // If 401, refresh token and retry once
  if (response.status === 401) {
    const newToken = await refreshAccessToken();

    if (newToken) {
      // Retry with new token
      response = await fetch(url, {
        ...options,
        headers: {
          ...defaultHeaders,
          Authorization: `Bearer ${newToken}`,
          ...options.headers,
        },
      });
    }
  }

  return response;
};

/**
 * Get current user details from localStorage
 * @returns {object} User details object
 */
export const getUserDetails = () => {
  return JSON.parse(localStorage.getItem(STORAGE_KEYS.SESSION) || "{}");
};

/**
 * Check if current user is admin
 * @returns {boolean} True if user has admin privileges
 */
export const isUserAdmin = () => {
  const userDetails = getUserDetails();
  return userDetails?.is_admin || false;
};
